// ResizableEventContainer.tsx
import React, {
  CSSProperties,
  ReactElement,
  ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import moment, { Moment } from "moment";

import MouseHoverTooltip from "./MouseHoverTooltip";

export type ResizableEventContainerProps = {
  className?: string;
  style?: CSSProperties;
  children: ReactNode;
  disabled?: boolean;
  endTime?: Date | Moment;
  hourHeight?: number;
  maxTime?: string;
  minDurationMinutes?: number;
  minTime?: string;
  minutesPerStep?: number;
  startTime?: Date | Moment;
  tooltip?: ReactElement | string;
  onClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
  onResizeEnd?: (data: ResizeParamsType) => void;
  onResizing?: (data: ResizeParamsType) => void;
};

export type ResizeParamsType = { durationMinutes: number; endTime: Moment; startTime: Moment };

const ResizableEventContainer = (props: ResizableEventContainerProps) => {
  const [isResizing, setIsResizing] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const rbcEventRef = useRef<HTMLElement | null>(null);
  const calStartTimeRef = useRef<number | null>(null);
  const calEndTimeRef = useRef<number | null>(null);

  const hourHeight = props.hourHeight ?? 70;
  const stepMinutes = props.minutesPerStep ?? 5;
  const minDuration = props.minDurationMinutes ?? 5;
  const minTime = props.minTime ?? "08:00";
  const maxTime = props.maxTime ?? "20:59";

  const PIXEL_PER_STEP = hourHeight / 12;
  const MIN_HEIGHT = (minDuration / stepMinutes) * PIXEL_PER_STEP;

  useEffect(() => {
    const [startHours, startMinutes] = minTime.split(":").map(Number);

    calStartTimeRef.current = startHours * 60 + startMinutes;

    const [endHours, endMinutes] = maxTime.split(":").map(Number);

    calEndTimeRef.current = endHours * 60 + endMinutes;
  }, [maxTime, minTime]);

  const calculatedDimensions = useMemo(() => {
    if (!props.startTime || !props.endTime || calStartTimeRef.current === null) {
      return { height: "100%", top: null };
    }

    const start = moment(props.startTime);
    const end = moment(props.endTime);
    const durationMinutes = end.diff(start, "minutes");

    const height = (durationMinutes / 60) * hourHeight;

    const startOfDay = moment(start).startOf("day");
    const minsFromMidnight = start.diff(startOfDay, "minutes");

    const minsFromCalStart = minsFromMidnight - calStartTimeRef.current;
    const top = Math.max(0, (minsFromCalStart / 60) * hourHeight);

    return { height, top };
  }, [hourHeight, props.endTime, props.startTime]);

  useEffect(() => {
    if (containerRef.current) {
      const rbcEvent = containerRef.current.closest(".rbc-event") as HTMLDivElement | null;

      if (rbcEvent) {
        rbcEventRef.current = rbcEvent as HTMLElement;

        const daySlot = rbcEvent.closest(".rbc-day-slot");

        if (daySlot && !rbcEvent.dataset.slotHeight) {
          rbcEvent.dataset.slotHeight = String(daySlot.clientHeight);
        }
      }
    }
  }, []);

  useEffect(() => {
    if (rbcEventRef.current && typeof calculatedDimensions.height === "number") {
      const slotHeight = rbcEventRef.current.dataset.slotHeight;

      if (slotHeight) {
        const heightPct = (calculatedDimensions.height / Number.parseFloat(slotHeight)) * 100;

        rbcEventRef.current.style.height = `${heightPct}%`;

        if (calculatedDimensions.top !== null) {
          const topPct = (calculatedDimensions.top / Number.parseFloat(slotHeight)) * 100;

          rbcEventRef.current.style.top = `${topPct}%`;
        }
      }
    }
  }, [calculatedDimensions]);

  // Helper: อัพเดท style
  const applyStyle = (height: number, top?: number) => {
    if (!rbcEventRef.current) {
      return;
    }

    const slotHeight = rbcEventRef.current.dataset.slotHeight;

    if (!slotHeight) {
      return;
    }

    const slotH = Number.parseFloat(slotHeight);

    rbcEventRef.current.style.height = `${(height / slotH) * 100}%`;

    if (top !== undefined) {
      rbcEventRef.current.style.top = `${(top / slotH) * 100}%`;
    }
  };

  // Helper: คำนวณ time จาก pixel
  const convertPxToTime = (topPx: number, heightPx: number): ResizeParamsType | null => {
    if (calStartTimeRef.current === null) {
      return null;
    }

    const topSteps = Math.round(topPx / PIXEL_PER_STEP);
    const heightSteps = Math.round(heightPx / PIXEL_PER_STEP);

    const topMins = topSteps * stepMinutes;
    const durationMins = heightSteps * stepMinutes;

    const baseTime = props.startTime ?? props.endTime;

    if (!baseTime) {
      return null;
    }

    const startOfDay = moment(baseTime).startOf("day");
    const startMinsFromMidnight = calStartTimeRef.current + topMins;
    const start = startOfDay.clone().add(startMinsFromMidnight, "minutes");
    const end = start.clone().add(durationMins, "minutes");

    return {
      durationMinutes: durationMins,
      endTime: end,
      startTime: start,
    };
  };

  // Helper: จัดการ cleanup และ callback
  const completeResize = (
    finalTop: number,
    finalHeight: number,
    onComplete?: (data: ResizeParamsType) => void
  ) => {
    setTimeout(() => setIsResizing(false), 100);

    if (!onComplete) {
      return;
    }

    const timeData = convertPxToTime(finalTop, finalHeight);

    if (timeData) {
      onComplete(timeData);
    }
  };

  // Helper: คำนวณตำแหน่งและขนาดใหม่
  const calculateDimensions = (
    deltaY: number,
    type: "bottom" | "top",
    startTop: number,
    startH: number
  ) => {
    let top: number;
    let height: number;

    if (type === "top") {
      top = startTop + deltaY;
      height = startH - deltaY;
    } else {
      top = startTop;
      height = startH + deltaY;
    }

    const snappedTop = Math.round(top / PIXEL_PER_STEP) * PIXEL_PER_STEP;
    const snappedHeight = Math.round(height / PIXEL_PER_STEP) * PIXEL_PER_STEP;

    return {
      height: Math.max(MIN_HEIGHT, snappedHeight),
      top: Math.max(0, snappedTop),
    };
  };

  // Helper: จำกัดการ resize หัว (top)
  const applyTopConstraints = (top: number, height: number, startTop: number, startH: number) => {
    if (!props.endTime || calStartTimeRef.current === null || calEndTimeRef.current === null) {
      return { height, top };
    }

    const end = moment(props.endTime);
    const startOfDay = moment(end).startOf("day");
    const endMinsFromMidnight = end.diff(startOfDay, "minutes");

    const topMins = Math.round(top / PIXEL_PER_STEP) * stepMinutes;
    const startMinsFromMidnight = calStartTimeRef.current + topMins;

    // เช็คไม่ให้ต่ำกว่า minTime
    if (startMinsFromMidnight < calStartTimeRef.current) {
      return { height: startH + startTop, top: 0 };
    }

    // เช็คไม่ให้ duration ต่ำกว่า min
    const durationMins = endMinsFromMidnight - startMinsFromMidnight;

    if (durationMins < minDuration) {
      const maxTop =
        ((endMinsFromMidnight - calStartTimeRef.current - minDuration) / 60) * hourHeight;

      return {
        height: Math.max(MIN_HEIGHT, startTop + startH - top),
        top: Math.min(top, maxTop),
      };
    }

    return { height, top };
  };

  // Helper: จำกัดการ resize ท้าย (bottom)
  const applyBottomConstraints = (height: number) => {
    if (!props.startTime || calStartTimeRef.current === null || calEndTimeRef.current === null) {
      return height;
    }

    const start = moment(props.startTime);
    const startOfDay = moment(start).startOf("day");
    const startMinsFromMidnight = start.diff(startOfDay, "minutes");

    const durationMins = Math.round(height / PIXEL_PER_STEP) * stepMinutes;
    const endMinsFromMidnight = startMinsFromMidnight + durationMins;

    // เช็คไม่ให้เกิน maxTime
    if (endMinsFromMidnight > calEndTimeRef.current) {
      const maxDuration = calEndTimeRef.current - startMinsFromMidnight;

      return (maxDuration / 60) * hourHeight;
    }

    return height;
  };

  // Helper: สร้าง resize handler
  const createResizeHandler = (type: "bottom" | "top") => (e: React.MouseEvent) => {
    if (props.disabled) {
      return;
    }

    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);

    const startY = e.clientY;
    const startTop = rbcEventRef.current?.offsetTop ?? 0;
    const startH = rbcEventRef.current?.offsetHeight ?? 0;

    const handleMove = (moveEvent: MouseEvent) => {
      const deltaY = moveEvent.clientY - startY;

      // คำนวณตำแหน่งและขนาดใหม่
      let { height, top } = calculateDimensions(deltaY, type, startTop, startH);

      // จำกัดตาม constraints
      if (type === "top") {
        ({ height, top } = applyTopConstraints(top, height, startTop, startH));
      } else {
        height = applyBottomConstraints(height);
      }

      applyStyle(height, type === "top" ? top : undefined);

      // Callback ระหว่าง resize
      if (props.onResizing) {
        const timeData = convertPxToTime(top, height);

        if (timeData) {
          props.onResizing(timeData);
        }
      }
    };

    const handleUp = () => {
      document.removeEventListener("mousemove", handleMove);
      document.removeEventListener("mouseup", handleUp);

      const finalTop = rbcEventRef.current?.offsetTop ?? startTop;
      const finalH = rbcEventRef.current?.offsetHeight ?? startH;

      completeResize(finalTop, finalH, props.onResizeEnd);
    };

    document.addEventListener("mousemove", handleMove);
    document.addEventListener("mouseup", handleUp);
  };

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isResizing && props.onClick) {
      props.onClick(e);
    }
  };

  const handleMouseEnter = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!props.disabled) {
      e.currentTarget.style.backgroundColor = "rgba(243, 130, 166, 0.1)";
    }
  };

  const handleMouseLeave = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!props.disabled) {
      e.currentTarget.style.backgroundColor = "transparent";
    }
  };

  return (
    <MouseHoverTooltip content={props.tooltip}>
      <div
        ref={containerRef}
        className={props.className}
        style={{
          height: "100%",
          minWidth: "125px",
          overflowY: "auto",
          padding: "4px 8px",
          position: "relative",
          ...props.style,
        }}
        onClick={handleClick}
      >
        {props.children}

        {/* Top Handle */}
        {!props.disabled && (
          <div
            style={{
              backgroundColor: "transparent",
              cursor: "ns-resize",
              height: "12px",
              left: 0,
              position: "absolute",
              right: 0,
              top: 0,
              zIndex: 10,
            }}
            onMouseDown={createResizeHandler("top")}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          />
        )}

        {/* Bottom Handle */}
        {!props.disabled && (
          <div
            style={{
              backgroundColor: "transparent",
              bottom: 0,
              cursor: "ns-resize",
              height: "12px",
              left: 0,
              position: "absolute",
              right: 0,
              zIndex: 10,
            }}
            onMouseDown={createResizeHandler("bottom")}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          />
        )}
      </div>
    </MouseHoverTooltip>
  );
};

ResizableEventContainer.displayName = "ResizableEventContainer";

export default React.memo(ResizableEventContainer);
